import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ScrollText, Search, User, Clock, Filter,
  FileText, Send, CheckCircle2, RotateCcw, MessageSquare,
  Package, PenSquare, Sparkles, ArrowRightLeft,
} from 'lucide-react';

/**
 * Human-readable icon + colour per activity/audit action.
 */
const ACTION_META = {
  created:            { icon: PenSquare,     color: '#4f46e5', label: 'Pack created' },
  updated:            { icon: PenSquare,     color: '#0ea5e9', label: 'Pack updated' },
  status_change:      { icon: ArrowRightLeft,color: '#2563eb', label: 'Status changed' },
  lodged:             { icon: Send,          color: '#0891b2', label: 'Lodged with lender' },
  reopened:           { icon: RotateCcw,     color: '#d97706', label: 'File reopened' },
  completed:          { icon: CheckCircle2,  color: '#059669', label: 'File completed' },
  mir_added:          { icon: FileText,      color: '#7c3aed', label: 'MIR added' },
  mir_updated:        { icon: FileText,      color: '#7c3aed', label: 'MIR updated' },
  mir_completed:      { icon: CheckCircle2,  color: '#059669', label: 'MIR completed' },
  mir_deleted:        { icon: FileText,      color: '#e11d48', label: 'MIR deleted' },
  comment_added:      { icon: MessageSquare, color: '#0891b2', label: 'Comment added' },
  docs_received:      { icon: Package,       color: '#22c55e', label: 'Docs received' },
  returned_to_lender: { icon: Send,          color: '#065f46', label: 'Returned to lender' },
  smart_import:       { icon: Sparkles,      color: '#8b5cf6', label: 'Smart Import run' },
};

const humanTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function AuditLogDialog({ open, onClose, pack }) {
  const [q, setQ] = useState('');
  const [action, setAction] = useState('all');
  const [author, setAuthor] = useState('all');

  const events = useMemo(() => {
    const raw = (pack?.activity || []).slice().reverse(); // newest first
    return raw.filter((e) => {
      if (action !== 'all' && (e.action || 'updated') !== action) return false;
      if (author !== 'all' && (e.user || '') !== author) return false;
      if (q.trim()) {
        const hay = `${e.action || ''} ${e.details || ''} ${e.user || ''} ${e.before || ''} ${e.after || ''}`.toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [pack, q, action, author]);

  const distinctActions = useMemo(() => {
    const set = new Set((pack?.activity || []).map((e) => e.action || 'updated'));
    return Array.from(set);
  }, [pack]);
  const distinctAuthors = useMemo(() => {
    const set = new Set((pack?.activity || []).map((e) => e.user).filter(Boolean));
    return Array.from(set);
  }, [pack]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden bg-white"
        style={{ colorScheme: 'light' }}
        data-testid="audit-log-dialog"
      >
        <DialogHeader className="p-0">
          <div className="px-5 py-3.5 border-b flex items-start gap-3" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-indigo-600">
              <ScrollText className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-black text-slate-900">Audit Log</DialogTitle>
              <DialogDescription className="text-[12px] font-semibold text-slate-500 mt-0.5">
                Every recorded action on this client&apos;s pack — {events.length} of {(pack?.activity || []).length} events shown
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Filters */}
        <div className="px-5 py-3 border-b grid grid-cols-1 sm:grid-cols-[1fr_180px_180px] gap-2" style={{ borderColor: '#e2e8f0', background: '#ffffff' }}>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search action, user, details…"
              className="h-9 pl-8 text-xs bg-white text-slate-900"
              data-testid="audit-log-search"
            />
          </div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 text-xs bg-white text-slate-900" data-testid="audit-log-action-filter">
              <Filter className="w-3.5 h-3.5 mr-1 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {distinctActions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_META[a]?.label || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={author} onValueChange={setAuthor}>
            <SelectTrigger className="h-9 text-xs bg-white text-slate-900" data-testid="audit-log-author-filter">
              <User className="w-3.5 h-3.5 mr-1 text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All users</SelectItem>
              {distinctAuthors.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Feed */}
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {events.length === 0 ? (
            <div className="text-center py-10">
              <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-black text-slate-800">No events match your filters</p>
              <p className="text-[12px] text-slate-500 mt-1">Try clearing the search or picking &ldquo;All&rdquo; in the dropdowns.</p>
            </div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
              {events.map((e, idx) => {
                const meta = ACTION_META[e.action || 'updated'] || { icon: PenSquare, color: '#64748b', label: e.action || 'Updated' };
                const Icon = meta.icon;
                return (
                  <li key={idx} className="relative pl-5" data-testid={`audit-log-event-${idx}`}>
                    <span
                      className="absolute -left-[13px] top-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                      style={{ background: meta.color }}
                    >
                      <Icon className="w-3 h-3 text-white" />
                    </span>
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: `${meta.color}18`, color: meta.color }}>
                        {meta.label}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-500">{humanTime(e.ts)}</span>
                      {e.user && (
                        <span className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                          <User className="w-2.5 h-2.5 text-slate-400" />
                          {e.user}
                        </span>
                      )}
                    </div>
                    {e.details && (
                      <p className="text-[12.5px] leading-snug text-slate-800">{e.details}</p>
                    )}
                    {(e.before || e.after) && (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {e.before && <span className="line-through mr-1.5">{e.before}</span>}
                        {e.after && <span className="font-semibold text-slate-700">→ {e.after}</span>}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
